import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  FormGroup,
  FormFeedback,
  FormText,
  Label,
  Input,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import AdminEditButtons from '../EditButtons';

import { userIsAdmin } from '../../../helpers/Auth';
import { getSetting } from '../../../helpers/Settings';

import ErrorBoundary from '../../ErrorBoundary';

const AdminUserEdit2FA = (props) => {
  const {
    profile,
    isSelfEdit,
    errors,
    handleChange,
    handleRemove2FA,
  } = props;
  const intl = useIntl();

  const [showSecretCode, setShowSecretCode] = useState(false);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [removeButtonDisabled, setRemoveButtonDisabled] = useState(false);

  const revealSecretCode = (e) => {
    e.preventDefault();
    setShowSecretCode(true);
  };

  const switchRecoveryCodes = (e) => {
    e.preventDefault();
    setShowRecoveryCodes(!showRecoveryCodes);
  };

  return (
    <ErrorBoundary>
      <div className="tab-content-wrapper">
        {(getSetting('2fa_required') && !profile.g2fa_enabled) && (
          <Alert color="danger">
            {intl.formatMessage({
              defaultMessage: '2FA is required. You need to set it up before you can continue using this workspace.',
            })}
          </Alert>
        )}
        <h4>
          {intl.formatMessage({ defaultMessage: 'Two-Factor Authentication (2FA)' })}
        </h4>
        {isSelfEdit ? (
          <p>
            {profile.g2fa_enabled ? (
              intl.formatMessage({ defaultMessage: 'Two-factor authentication is enabled for your Stockpress account.' })
            ) : (
              intl.formatMessage({ defaultMessage: 'Set up two-factor authentication for your Stockpress account.' })
            )}
          </p>
        ) : (
          <p>
            {profile.g2fa_enabled ? (
              intl.formatMessage({ defaultMessage: 'This user already set up two-factor authentication.' })
            ) : (
              intl.formatMessage({ defaultMessage: 'This user did not set up two-factor authentication.' })
            )}
          </p>
        )}
        {isSelfEdit && (
          <div className="user-2fa">
            {profile.g2fa_enabled ? (
              <>
                <div className="remove-2fa">
                  <Button
                    type="button"
                    color="danger"
                    disabled={removeButtonDisabled}
                    onClick={() => {
                      setRemoveButtonDisabled(true);
                      handleRemove2FA();
                    }}
                  >
                    {intl.formatMessage({ defaultMessage: 'Remove 2FA' })}
                  </Button>
                </div>
                {profile.g2fa_recovery_codes && (
                  <div className="recovery-codes">
                    <h5>{intl.formatMessage({ defaultMessage: 'Recovery codes' })}</h5>
                    <p>
                      {intl.formatMessage({
                        defaultMessage: `If you lose access to your 2FA credentials, you
                          can use the below one-time use recovery codes to
                          access your account. Please copy these codes and save them in a secure place.`,
                      })}
                    </p>
                    {showRecoveryCodes ? (
                      <a href="#recovery-codes" onClick={switchRecoveryCodes}>
                        {intl.formatMessage({ defaultMessage: 'Hide recovery codes' })}
                      </a>
                    ) : (
                      <a href="#recovery-codes" onClick={switchRecoveryCodes}>
                        {intl.formatMessage({ defaultMessage: 'Show recovery codes' })}
                      </a>
                    )}
                    {showRecoveryCodes && (
                      <ul className="recovery-codes-list code">
                        {profile.g2fa_recovery_codes.map(rc => (
                          <li>{rc}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <p>
                  {intl.formatMessage({ defaultMessage: 'Scan the QR code below using your 2FA app.' })}{' '}
                  <a href="https://help.stockpress.co/en/articles/6433769-two-factor-authentication-2fa" target="_blank" rel="noopener noreferrer">
                    {intl.formatMessage({ defaultMessage: 'Click here' })}
                  </a>{' '}
                  {intl.formatMessage({
                    defaultMessage: `to learn more and for a list of authenticator apps.{br}
                      If you can't scan the QR code, you can enter your secret key manually.`,
                  }, { br: <br /> })}{' '}
                  {!showSecretCode && (
                    <a href="#secret" onClick={revealSecretCode}>
                      {intl.formatMessage({ defaultMessage: 'Reveal secret key' })}
                    </a>
                  )}
                </p>
                {showSecretCode && (
                  <p>
                    {intl.formatMessage({ defaultMessage: 'Your secret key:' })}{' '}
                    <span className="code">{profile.g2fa_secret}</span>
                  </p>
                )}
                {profile.g2fa_qrcode && (
                  <div className="g2fa-qrcode">
                    <img src={`data:image/png;base64, ${profile.g2fa_qrcode} `} alt="" />
                  </div>
                )}
                <FormGroup>
                  <Label for="inp2FACode">
                    {intl.formatMessage({ defaultMessage: 'Enter the code from the application' })}
                  </Label>
                  <Input
                    type="text"
                    name="g2fa_code"
                    id="inp2FACode"
                    className="g2fa-code"
                    maxLength="6"
                    placeholder={intl.formatMessage({ defaultMessage: '6-digit code' })}
                    onChange={handleChange}
                    invalid={Boolean(errors.g2fa_code)}
                  />
                  {errors.g2fa_code && (
                    <FormFeedback>{errors.g2fa_code}</FormFeedback>
                  )}
                  <FormText>
                    {intl.formatMessage({
                      defaultMessage: `After scanning the QR code image,
                        the app will display a code that you can enter above.`,
                    })}
                  </FormText>
                </FormGroup>
              </>
            )}
          </div>
        )}
        {(!isSelfEdit && userIsAdmin() && profile.g2fa_enabled) && (
          <div className="remove-2fa">
            <Button
              type="button"
              color="danger"
              disabled={removeButtonDisabled}
              onClick={() => {
                setRemoveButtonDisabled(true);
                handleRemove2FA();
              }}
            >
              {intl.formatMessage({ defaultMessage: 'Remove 2FA' })}
            </Button>
          </div>
        )}
      </div>
      <AdminEditButtons
        {...props}
        hideDelete={true}
      />
    </ErrorBoundary>
  );
};

AdminUserEdit2FA.propTypes = {
  profile: PropTypes.object.isRequired,
  isSelfEdit: PropTypes.bool.isRequired,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleRemove2FA: PropTypes.func.isRequired,
};

export default AdminUserEdit2FA;
